<template>
    <div class="d-flex ml-auto layers-header px-2 py-4 align-items-center justify-content-between">
        <div class="d-flex align-items-center">
            <span>{{ title }}</span>
            <div v-if="isPreviewStep" class="ml-2">
                <layers-header-list-preview />
            </div>
        </div>
        <div v-if="!isReadonly" class="d-flex align-items-center">
            <template v-if="canvasElements && canvasElements.length">
                <c-button
                    class="btn"
                    size="icon"
                    title="Find in Layers"
                    variant="light"
                    :icon="['fas', 'search']"
                    @click.stop="emit('open-find-layers')"
                />
                <button
                    v-if="!areAllElementsUnSelected && !areAllElementsSelected"
                    class="btn btn-link"
                    @click.stop="selectAllUnlockedElements"
                >
                    Select unlocked
                </button>
                <button
                    v-if="areAllElementsUnSelected && !areAllElementsSelected"
                    class="btn btn-link"
                    @click.stop="selectAllElements"
                >
                    Select all
                </button>
                <button v-if="areAllElementsSelected" class="btn btn-link" @click.stop="clearSelection">
                    Clear selection
                </button>
                <div class="border mx-2" />
            </template>
            <template v-if="bulkToggleElements && bulkToggleElements.length">
                <div v-if="areAllSelectedElementsUnlocked" class="d-flex align-items-center">
                    <c-button
                        v-if="areAllSelectedElementsHidden"
                        v-tippy="{ placement: 'top', interactive: false }"
                        data-testid="bulk-view"
                        :data-dd-action-name="`View selected${bulkActionSuffix}`"
                        content="View selected"
                        size="icon"
                        :icon="['far', 'eye']"
                        @click.stop="toggleAllVisible()"
                    />
                    <c-button
                        v-else
                        v-tippy="{ placement: 'top', interactive: false }"
                        data-testid="bulk-hide"
                        :data-dd-action-name="`Hide selected${bulkActionSuffix}`"
                        content="Hide selected"
                        size="icon"
                        :icon="['far', 'eye-slash']"
                        @click.stop="toggleAllVisible()"
                    />
                </div>
                <div class="d-flex align-items-center">
                    <c-button
                        v-if="!areAllSelectedElementsLocked"
                        v-tippy="{ placement: 'top', interactive: false }"
                        data-testid="bulk-lock"
                        :data-dd-action-name="`Lock selected${bulkActionSuffix}`"
                        content="Lock selected"
                        size="icon"
                        :icon="['far', 'lock']"
                        @click.stop="toggleAllLock()"
                    />
                    <c-button
                        v-else
                        v-tippy="{ placement: 'top', interactive: false }"
                        data-testid="bulk-unlock"
                        :data-dd-action-name="`Unlock selected${bulkActionSuffix}`"
                        content="Unlock selected"
                        size="icon"
                        :icon="['far', 'lock-open']"
                        @click.stop="toggleAllLock()"
                    />
                </div>
            </template>
            <div v-if="flags?.isAbleToGroupOrUnGroup && getActiveLayers && getActiveLayers.length" class="divider" />
            <template v-if="flags?.isAbleToGroupOrUnGroup">
                <div v-if="flags?.showGroups" class="layout-btns-container">
                    <c-button
                        v-if="showGroupButton"
                        v-tippy="{ placement: 'bottom', interactive: false }"
                        data-dd-action-name="Create Layout"
                        content="Create Layout (Ctrl+L)"
                        size="icon"
                        :icon="['fak', 'branch']"
                        @click.stop="
                            groupSelectedItems({
                                isLayout: true
                            })
                        "
                    />
                    <c-button
                        v-if="showGroupButton"
                        v-tippy="{ placement: 'bottom', interactive: false }"
                        data-dd-action-name="Create Group"
                        content="Create Group (Ctrl+G)"
                        size="icon"
                        :icon="['far', 'layer-group']"
                        @click.stop="
                            groupSelectedItems({
                                isLayout: false
                            })
                        "
                    />
                    <c-button
                        v-if="showUnGroupButton"
                        v-tippy="{ placement: 'bottom', interactive: false }"
                        :data-dd-action-name="isLayout ? 'Remove Layout' : 'Remove Group'"
                        :content="isLayout ? 'Remove Layout (Ctrl+Shift+G)' : 'Remove Group (Ctrl+Shift+G)'"
                        size="icon"
                        :icon="['fak', isLayout ? 'remove-layout' : 'ungroup']"
                        @click.stop="unGroupSelectedItems()"
                    />
                </div>
                <div
                    v-else
                    v-tippy
                    class="ml-3 heading-section d-flex"
                    content="Your current plan doesn't include this feature. Please check your upgrade options with our supoport team :)"
                >
                    <a v-if="showGroupButton" class="disabled">Create Layout</a>
                </div>
            </template>

            <c-dropdown
                v-if="areAllElementsUnSelected && flags?.enableImportLayers"
                label="Import"
                variant="empty"
                :items="importFromOptions"
            >
                <template #dropdown-menu-item="{ thumb, label }">
                    <div class="d-flex align-items-center w-100 cursor-pointer">
                        <div v-if="thumb" class="d-flex align-items-center mr-2">
                            <svg-icon :src="thumb" />
                        </div>
                        <span class="w-100">{{ label }}</span>
                    </div>
                </template>
            </c-dropdown>

            <modal-import
                :is-open="isModalImportOpen"
                :pre-selected-tab="preSelectedTab"
                :handle-close="closeModalImport"
                :handle-open="openModalImport"
                @import-layers="importLayers"
            />
            <input id="importFromFile" type="file" accept=".psd" value="upload" hidden @change="uploadPsdFile" />
            <input
                id="importAmazonFromFile"
                type="file"
                accept=".xlsx"
                value="upload"
                hidden
                @change="uploadAmazonFile"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import store from '@/store';
import SvgIcon from '@/components/SvgIcon/SvgIcon.vue';
import CDropdown from '@/components/shared/CDropdown/CDropdown.vue';
import ModalImport from '@/components/Layers/modal/import/ModalImport.vue';

import { canGroupSelectedItems, canUnGroupSelectedItems } from '@/utils/layers/groupUtils';
import templateSvg from '@/assets/images/template.svg';
import canvaSvg from '@/assets/images/canva-logo.svg';
import psdSvg from '@/assets/images/photoshop.svg';
import amazonSvg from '@/assets/images/amazon-icon.svg';
import { useFileLayers } from '@/composables/externalFileParser';
import { CDropdownItem } from '@/components/shared/CDropdown';
import { ImportLayersEventData } from '@/typings/layers/layers';
import { Tabs } from '../modal/types';
import { useCanva } from '@/composables/canva';
import CButton from '@/components/shared/CButton/CButton.vue';
import LayersHeaderListPreview from '@/components/Layers/header/list-preview/LayersHeaderListPreview.vue';
import { ListElementType } from '@/typings/customilyElements/listElementType';

const { data: psdLayers, parseAndExtractLayers } = useFileLayers('PSD');
const { linkAccountToCanva } = useCanva(
    () => emit('user-updated'),
    () => preSelectTab(Tabs.Canva)
);

const showLoader = ref(false);
const isModalImportOpen = ref(false);
const preSelectedTab = ref<Tabs | undefined>(undefined);
const importFromOptions: CDropdownItem[] = [
    {
        label: 'Layers from Design/Template',
        thumb: templateSvg,
        callback: () => preSelectTab(Tabs.Templates)
    },
    {
        label: 'PSD File',
        thumb: psdSvg,
        divider: true,
        callback: () => fireInputPsdFileClick()
    },
    {
        label: 'From Canva',
        thumb: canvaSvg,
        divider: true,
        callback: () => linkAccountToCanva()
    },
    {
        label: 'Amazon File (.xlsx)',
        thumb: amazonSvg,
        divider: true,
        callback: () => fireInputAmazonFileClick()
    }
];
const props = defineProps<{
    startWithCanva?: boolean;
    title?: string;
}>();

const emit = defineEmits(['import-layers', 'user-updated', 'open-find-layers']);

watch(
    () => [store.state.user, props.startWithCanva],
    async ([user, startWithCanva]) => {
        if (!user || !startWithCanva) return;
        await linkAccountToCanva();
    },
    { immediate: true }
);

const canvasElements = computed(() => {
    return store.state.storeLayers?.rawElements.canvasElements;
});

const flags = computed(() => {
    return store.state.storeLayers?.flags;
});

const isPreviewStep = computed(() => {
    return flags.value?.isPreviewStep;
});

const isReadonly = computed(() => {
    return flags.value?.readonly;
});

const areAllElementsSelected = computed(() => {
    return store.getters['storeLayers/areAllElementsSelected'];
});

const areAllElementsUnSelected = computed(() => {
    return store.getters['storeLayers/areAllElementsUnSelected'];
});

const getActiveLayers = computed(() => {
    return store.getters['storeLayers/getAllSelectedCanvasElements'];
});

// Get active canvas elements (selected elements + children of selected groups/layouts/files)
// used only to gate the bulk visibility/lock buttons.
const bulkToggleElements = computed(() => {
    return store.getters['storeLayers/getActiveCanvasElements'];
});

// Suffix appended to the bulk action names reported to Datadog: when at least one
// container (file, layout or group) is selected we report the usual action with a
// "- Group" suffix; a selection of only loose elements keeps the plain action name.
const bulkActionSuffix = computed(() => {
    const selectedItems = store.state.storeLayers?.selectedItems;
    const hasContainer = selectedItems?.some((item) =>
        [ListElementType.Group, ListElementType.PageEps].includes(item.type)
    );
    return hasContainer ? ' - Group' : '';
});

const areAllSelectedElementsUnlocked = computed(() => {
    return store.getters['storeLayers/areAllSelectedElementsUnlocked'];
});

const areAllSelectedElementsLocked = computed(() => {
    return store.getters['storeLayers/areAllSelectedElementsLocked'];
});

const areAllSelectedElementsHidden = computed(() => {
    return store.getters['storeLayers/areAllSelectedElementsHidden'];
});

const showGroupButton = computed(() => {
    if (!store.state.storeLayers?.canvas) {
        return false;
    }
    return canGroupSelectedItems(store.state.storeLayers?.canvas);
});

const showUnGroupButton = computed(() => {
    if (!store.state.storeLayers?.canvas) {
        return false;
    }
    return canUnGroupSelectedItems(store.state.storeLayers?.canvas);
});

const isLayout = computed(() => {
    const selectedItems = store.state.storeLayers?.selectedItems;
    if (!selectedItems?.length) return false;
    const groups = selectedItems.filter((item) => item.type === ListElementType.Group);
    if (groups.length === 0) return false;
    return groups.every((item) => item.element?.isLayout);
});

const importLayers = (data: ImportLayersEventData) => {
    const { handleClose, handleError, preserveId, preserveGroups, importedFileName } = data;
    emit('import-layers', {
        preserveId,
        preserveGroups,
        importedFileName: importedFileName,
        asPng: data.asPng,
        from: data.from,
        canvasElements: data.canvasElements,
        canvasGroups: data.canvasGroups,
        fileElements: data.fileElements,
        fileDimensions: data.fileDimensions,
        pageBackgroundColor: data.pageBackgroundColor,
        errorMessage: data.errorMessage,
        notFoundFonts: data.notFoundFonts,
        handleClose,
        handleError
    });
};

const selectAllUnlockedElements = (): void => {
    store.dispatch('storeLayers/selectAllUnlockedElements');
};

const selectAllElements = (): void => {
    store.dispatch('storeLayers/selectAllElements');
};

const clearSelection = (): void => {
    store.dispatch('storeLayers/clearSelectedItems');
    store.state.storeLayers?.canvas?.discardActiveObject();
};

const toggleAllVisible = (): void => {
    store.dispatch('storeLayers/toggleAllVisible');
};

const toggleAllLock = (): void => {
    store.dispatch('storeLayers/toggleAllLock');
};

const groupSelectedItems = (props: { isLayout: boolean }): void => {
    try {
        const { canvas } = store.state.storeLayers || {};
        canvas?.groupSelectedItems(props);
    } catch (error) {
        console.error('Error grouping selected elements:', error);
    }
};

const unGroupSelectedItems = (): void => {
    try {
        const { canvas } = store.state.storeLayers || {};
        canvas?.unGroupSelectedItems();
    } catch (error) {
        console.error('Error ungrouping selected elements:', error);
    }
};

const openModalImport = (): void => {
    isModalImportOpen.value = true;
};

const closeModalImport = (): void => {
    isModalImportOpen.value = false;
    preSelectedTab.value = undefined;
};

const preSelectTab = (tab: Tabs) => {
    openModalImport();
    preSelectedTab.value = tab;
};

const fireInputPsdFileClick = () => {
    const input = document.getElementById('importFromFile') as HTMLInputElement;
    if (!input) return;
    input.click();
};

const uploadPsdFile = async (event: Event) => {
    try {
        showLoader.value = true;
        const { files } = event.target as HTMLInputElement;
        if (!(files && files[0])) return;
        const file = files[0];
        if (file.type !== 'image/vnd.adobe.photoshop' && !file.name.endsWith('.psd')) {
            console.error('Invalid file type. Please select a PSD file.');
            return;
        }
        const { name } = file;
        const buffer = await file.arrayBuffer();
        await parseAndExtractLayers(buffer);
        store.dispatch('storeLayersModal/setParsedFileLayers', {
            ...psdLayers.value,
            artworkName: name,
            selectedPage: 1
        });
        preSelectTab(Tabs.ExternalLayers);
    } catch (error) {
        console.log('error', error);
    } finally {
        showLoader.value = false;
    }
};

const fireInputAmazonFileClick = () => {
    const input = document.getElementById('importAmazonFromFile') as HTMLInputElement;
    if (!input) return;
    input.click();
};

const uploadAmazonFile = async (event: Event) => {
    try {
        showLoader.value = true;
        const { files } = event.target as HTMLInputElement;
        if (!(files && files[0])) return;
        const file = files[0];

        if (
            file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
            !file.name.endsWith('.xlsx')
        ) {
            console.error('Invalid file type. Please select an Excel file.');
            return;
        }

        const buffer = await file.arrayBuffer();
        store.dispatch('storeLayersModal/setFileBuffer', buffer);
        preSelectTab(Tabs.AmazonFileXLSX);
    } catch (error) {
        console.error('Error parsing Excel file:', error);
        console.error('Error details:', {
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined
        });
    } finally {
        showLoader.value = false;
    }
};
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';
$border-color: $gray-light;

.layers-header {
    --primary-blue: #87add5;
    --secondary-color: #a7aab4;
    --secondary-hover-color: #868686;
    height: 32px;
    min-height: 32px;
    display: flex;
    align-items: center;
    width: 100%;

    background-color: #f5f5f5;
    border-bottom: 1px solid #ddd;

    .actionable-icon {
        cursor: pointer;
        color: var(--secondary-hover-color);
        font-size: 1rem;
        margin-left: 1rem;
    }

    .btn-link {
        box-shadow: none !important;
        color: var(--secondary-color);
        font-size: 0.75rem;
        font-weight: 600;
        padding-top: 0;
        padding-bottom: 0;
        line-height: 1;

        &:hover {
            color: var(--secondary-hover-color);
        }

        &.primary,
        &.primary:hover {
            padding-right: 0;
            color: var(--primary-blue);
            font-size: 0.875rem;
        }
    }
    .layout-btns-container {
        display: flex;
        align-items: center;
        margin-top: 0;
    }
    .divider {
        width: 1px;
        height: 32px;
        margin: 0 5px;
        background: $border-color;
    }
}
</style>
